import React, { useState } from "react";
import { ChevronDown, X, HelpCircle } from "lucide-react";

const FAQ_ITEMS = [
  {
    id: "faq-1",
    question: "How long does SIM delivery take?",
    answer: "We deliver your SIM card within 2 hours of order placement during our service hours (9AM–8PM). Our verified agent will call you before arriving at your address to confirm delivery.",
  },
  {
    id: "faq-2",
    question: "Which areas do you serve?",
    answer: "We currently serve Greater Noida, Ghaziabad, Noida, and select areas of Delhi. You can check if we serve your area by entering your pincode in the order form. We are expanding to more cities soon!",
  },
  {
    id: "faq-3",
    question: "What documents are needed for SIM activation?",
    answer: "You will need a valid government-issued photo ID (original ID verification) and proof of address. Our agent will guide you through the eKYC process at your doorstep — completely paperless.",
  },
  {
    id: "faq-4",
    question: "Can I keep my existing number? (Port-in / MNP)",
    answer: "Yes! We support Mobile Number Portability (MNP). Select the 'Port In' option when placing your order. You'll need to generate a Porting Code (UPC) by sending PORT <your 10-digit mobile number> to 1900 from your existing number.",
  },
  {
    id: "faq-5",
    question: "What is SIM Replacement service?",
    answer: "If you have lost your SIM card or it has been damaged, our SIM Replacement service helps you get a new SIM on the same number. The replacement fee is ₹299 + applicable FRC (First Recharge Charges) as per your chosen operator plan.",
  },
  {
    id: "faq-6",
    question: "How do I track my order?",
    answer: "Once your order is placed, you will receive an Order ID. Use the 'Track Order' page on our website and enter your Order ID or registered phone number to see real-time status updates — from order placed to delivery completed.",
  },
  {
    id: "faq-7",
    question: "What if my SIM is not activated on time?",
    answer: "We guarantee SIM delivery within 2 hours. If there is any delay, our support team will proactively reach out to you. You can also contact us via phone, WhatsApp, or email using the Contact Us button on our site.",
  },
  {
    id: "faq-8",
    question: "Do you support all major operators?",
    answer: "Yes! We currently support Jio, Airtel, Vi (Vodafone Idea), and BSNL. Each operator offers multiple plans with varying data, validity, and benefits. Click on any operator card on the homepage to see available plans.",
  },
];

export default function FAQSection() {
  const [selectedFaq, setSelectedFaq] = useState(null);

  return (
    <section id="faq" className="py-20 bg-slate-50/50">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <span className="inline-block px-4 py-1.5 rounded-full bg-orange-50 text-orange-600 text-xs font-bold uppercase tracking-wider mb-2">
            Got Questions?
          </span>
          <h2 className="font-extrabold text-3xl sm:text-4xl text-slate-900 mb-3 tracking-tight">
            Frequently Asked Questions
          </h2>
          <p className="text-slate-500 text-sm max-w-xl mx-auto">
            Click on any question to view detailed information in a pop-up window.
          </p>
        </div>

        <div className="space-y-3">
          {FAQ_ITEMS.map((item) => (
            <div
              key={item.id}
              onClick={() => setSelectedFaq(item)}
              className="rounded-2xl border border-slate-200 bg-white p-5 cursor-pointer hover:border-orange-400 hover:shadow-md transition-all flex items-center justify-between group"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-xl bg-orange-50 text-orange-600 flex items-center justify-center shrink-0 group-hover:bg-orange-600 group-hover:text-white transition-colors">
                  <HelpCircle size={16} />
                </div>
                <span className="font-semibold text-sm sm:text-base text-slate-900 group-hover:text-orange-600 transition-colors">
                  {item.question}
                </span>
              </div>
              <ChevronDown size={18} className="text-slate-400 group-hover:translate-x-1 transition-transform shrink-0" />
            </div>
          ))}
        </div>
      </div>

      {/* Professional FAQ Popup Modal */}
      {selectedFaq && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full p-6 sm:p-8 relative border border-white/80">
            <button
              onClick={() => setSelectedFaq(null)}
              className="absolute top-5 right-5 p-2 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-500 transition"
            >
              <X size={18} />
            </button>

            <div className="w-12 h-12 rounded-2xl bg-orange-50 text-orange-600 flex items-center justify-center mb-4 font-bold">
              ?
            </div>

            <h3 className="text-lg sm:text-xl font-bold text-slate-900 mb-3">
              {selectedFaq.question}
            </h3>
            
            <p className="text-sm text-slate-600 leading-relaxed mb-6 bg-slate-50 p-4 rounded-2xl border border-slate-100">
              {selectedFaq.answer}
            </p>

            <button
              onClick={() => setSelectedFaq(null)}
              className="w-full py-3 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-bold text-sm rounded-xl shadow-md transition"
            >
              Got it, thanks!
            </button>
          </div>
        </div>
      )}
    </section>
  );
}